/**
 * ============================================================================
 * useCreateProposalMutation — Phase 6c frontend (CCIE — Change Control Impact
 *   Engine).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/changes — propose a Change Control. The backend
 *          computes the Impact Set synchronously (CCIE compute SLO < 3 s; in
 *          practice sub-second) and returns the canonical change-id + the
 *          impact-set id so the UI can deep-link to the review surface.
 *
 * NOTE: This is a mutation rather than a query because creating a CC is a
 *  write that the user explicitly invokes. The UI shows "Computing impact…
 *  (sub-second)" while the request is in flight.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';

/**
 * The full set of CC types the CCIE supports. Owners must keep this list in
 * lock-step with the backend enum `change_request.change_type` — server
 * rejects unknown values with a 400.
 */
export type ChangeType =
  | 'DOCUMENT_CONTENT'
  | 'DOCUMENT_OWNERSHIP'
  | 'EQUIPMENT_RELOCATION'
  | 'EQUIPMENT_COMPONENT'
  | 'SUPPLIER_CHANGE'
  | 'SUPPLIER_MATERIAL'
  | 'VALIDATION_STATUS'
  | 'REGULATORY'
  | 'SYSTEM_CONFIG';

export interface CreateProposalInput {
  change_type: ChangeType;
  subject_entity_id: string;
  subject_entity_kind: string;
  title: string;
  rationale: string;
}

export interface CreateProposalResponse {
  change_id: string;
  impact_set_id: string;
  change_type: ChangeType;
  status: string;
  compute_duration_ms: number;
  item_count: number;
}

export function useCreateProposalMutation(): UseMutationResult<
  CreateProposalResponse,
  Error,
  CreateProposalInput
> {
  return useMutation<CreateProposalResponse, Error, CreateProposalInput>({
    mutationFn: (input) =>
      apiFetch<CreateProposalResponse>('/api/v1/changes', {
        method: 'POST',
        body: input,
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}
